import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, BedDouble, Globe, Loader2, Plane } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  pageDetailQueryOptions,
  publicEventPageDisplay,
} from "@/core/event-pages"
import { PageEditor } from "@/components/pages/PageEditor"

// Full-screen collaborative page editor — drilled from a Pages row (or the
// command palette). A distinct top segment (/p/$pageId) keeps the Pages list
// route (/pages) reachable, mirroring how forms uses /f/$formId and people uses
// /person/$guestId. The editor is full-bleed (toolbar + canvas), not a modal —
// matching the resizable-record convention the user prefers for content editing.
export const Route = createFileRoute("/$orgSlug/$eventSlug/p/$pageId")({
  component: PageRecordView,
})

function PageRecordView() {
  const { orgSlug, eventSlug, pageId } = useParams({
    from: "/$orgSlug/$eventSlug/p/$pageId",
  })
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const q = useQuery({ ...pageDetailQueryOptions(pageId), enabled: !!pageId })
  const page = q.data

  if (q.isLoading || !page) {
    return (
      <div className="flex h-full items-center justify-center gap-2 bg-white text-[13px] text-plum-700/55 dark:bg-surface dark:text-plum-200/45">
        {q.isError ? (
          <span>{(q.error as Error)?.message ?? "Failed to load page"}</span>
        ) : (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading page…
          </>
        )}
      </div>
    )
  }

  const isSplash = page.kind === "splash" || page.slug === "splash"
  const isTravel = page.kind === "travel"
  const isAccommodation = page.kind === "accommodation"
  const KindIcon = isTravel ? Plane : isAccommodation ? BedDouble : Globe
  const publicDisplay =
    orgSlug && eventSlug
      ? publicEventPageDisplay({
          orgSlug,
          eventSlug,
          pageSlug: page.slug,
          pageKind: page.kind ?? (isSplash ? "splash" : null),
        })
      : null

  return (
    <div className="flex h-full min-w-0 flex-col">
      {/* Record header — title + kind + public path + back-to-list. */}
      <div className="flex h-[52px] shrink-0 items-center gap-3 border-b border-plum-200/50 bg-white px-4 dark:border-white/10 dark:bg-surface">
        <Link
          to="/$orgSlug/$eventSlug/settings/site"
          params={{ orgSlug, eventSlug }}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-plum-700/60 transition hover:bg-plum-100/60 dark:text-plum-200/50 dark:hover:bg-white/5"
          title="Back to website settings"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <KindIcon className="h-4 w-4 shrink-0 text-[var(--accent)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
            {page.title || "Untitled page"}
          </div>
          {(isTravel || isAccommodation) && (
            <div className="truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
              In-app helper content
            </div>
          )}
          {!isTravel && !isAccommodation && publicDisplay && (
            <div className="truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
              {publicDisplay}
            </div>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="min-h-0 flex-1">
        <PageEditor
          page={page}
          event={{ eventId: eventId ?? page.eventId, orgSlug, eventSlug }}
          onPublished={() => {
            void qc.invalidateQueries({ queryKey: ["event-page", page.id] })
            if (eventId)
              void qc.invalidateQueries({ queryKey: ["event-pages", eventId] })
          }}
        />
      </div>
    </div>
  )
}
